import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Button, Modal, Stack, TextInput, Text } from "@mantine/core";
import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";
import { TextWithInfo } from "./textWithInfo";
import { sendToRailsController } from "../../helpers/requests";
import { frozen } from "mobx-keystone";
import SatoshisInfo from "../interactiveData/satoshisInfo";
import { isTwoFaSatisfied } from "../../helpers/sendInvoiceHelpers";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { IconAt } from "@tabler/icons-react";
import { ARTICLES } from "../../docs/docsIndex";
import { notifications } from "@mantine/notifications";

const ConfirmPayToLightningAddress = observer(({ cubby }: { cubby: Cubby }) => {
  const [satoshisAmount, setSatoshisAmount] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFAError, setTwoFAError] = useState<string | null>(null); // Error state for 2FA
  const [isDisabled, setIsDisabled] = useState<boolean>(true); // State to control button disabling

  useEffect(() => {
    cubby.cubbyFastInfo("does_need_2FA_form");
    console.log(
      "cubby.stagedLightningAddressDataForPayment:",
      cubby.stagedLightningAddressDataForPayment.data
    );
  }, []);

  useEffect(() => {
    // Disable the button if there are errors, or 2FA is not satisfied
    setIsDisabled(
      !!error ||
        !isTwoFaSatisfied(
          cubby.shouldShow2FAFormForPayments,
          twoFACode,
          twoFAError
        )
    );
  }, [error, cubby.shouldShow2FAFormForPayments, twoFACode, twoFAError]);

  const validateSatoshisAmount = (value: string) => {
    const amount = Number(value);
    if (!/^\d+$/.test(value)) {
      setError("Satoshis must be a valid integer number");
      return false;
    }

    const { minSendableSatoshis, maxSendableSatoshis } =
      cubby.stagedLightningAddressDataForPayment.data;

    const adjustedMaxAmount = Math.min(
      maxSendableSatoshis,
      cubby.outboundLiquidity
    ); /// cuz there will be routing fees also!!

    if (amount < minSendableSatoshis || amount > adjustedMaxAmount) {
      setError(
        `Amount must be between ${minSendableSatoshis} and ${adjustedMaxAmount.toLocaleString()} satoshis`
      );
      return false;
    }

    setError("");
    return true;
  };

  const handleTwoFACodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value.replace(/\D/g, ""); // Only allow digits
    setTwoFACode(value);

    // Validate if the code has exactly six digits
    if (value.length > 0 && value.length !== 6) {
      setTwoFAError("A 2FA Code must be six digits.");
    } else {
      setTwoFAError(null);
    }
  };

  const handleSendPayment = () => {
    if (!validateSatoshisAmount(satoshisAmount)) {
      return;
    }
    console.log(
      "in handleSendPayment with staged data:",
      cubby.stagedLightningAddressDataForPayment.data
    );
    notifications.show({
      color: "blue",
      title: "Payment starting",
      message: cubby.stagedLightningAddressDataForPayment.data.paymentAddress,
    });
    setLoading(true);
    const data = {
      cubbyUuid: cubby.cubbyUuid,
      rawCallbackUrl:
        cubby.stagedLightningAddressDataForPayment.data.rawCallBackUrl,
      satoshisAmount: parseInt(satoshisAmount, 10),
      comment: comment.trim() || null,
    };

    sendToRailsController(data, "/cubby_life/get_lightning_address_invoice")
      .then((response) => {
        console.log("response from get_lightning_address_invoice", response);
        if (response.error) {
          setError(response.error);
        } else {
          console.log("this is the response to use for the payment", response);
          if (response.error) {
            setError("An error occurred. Please try again.");
          }
          const payRequest = response.lightningInvoiceData.payRequest;
          cubby.setPayToLightningAddressInvoiceInPaymentProgress(payRequest);
          cubby.payToLightningAddressInvoice(
            payRequest,
            parseInt(satoshisAmount, 10),
            cubby.stagedLightningAddressDataForPayment.data
              .hTagInInvoiceMustMatch,
            twoFACode
          );

          // Introduce a timeout to keep loading true for 4 seconds
          setTimeout(() => {
            setLoading(false);
          }, 4000);
        }
      })
      .catch(() => {
        setError("An error occurred. Please try again.");
        setLoading(false); // Reset loading state immediately on error
      });
  };

  return (
    <>
      <Modal
        opened={cubby.currentModal === MODALS.CONFIRM_PAY_TO_LIGHTNING_ADDRESS}
        onClose={() => {
          cubby.setStagedLightningAddressDataForPayment({});
          cubby.setModal(null);
        }}
        title={
          <TextWithInfoAndIcon
            text="Pay To Lightning Address"
            articleSlug={ARTICLES.SENDING_TO_LIGHTING_ADDRESS.slug}
            icon={<IconAt size={24} />}
            cubby={cubby}
            textSize="lg"
          />
        }
      >
        <Stack>
          {cubby.stagedLightningAddressDataForPayment.data.paymentAddress && (
            <Text ta="center" size="md">
              {cubby.stagedLightningAddressDataForPayment.data.paymentAddress}
            </Text>
          )}

          {cubby.stagedLightningAddressDataForPayment.data
            .plainTextForUserDisplay && (
            <Text size="sm" ta="center" c="dimmed">
              {
                cubby.stagedLightningAddressDataForPayment.data
                  .plainTextForUserDisplay
              }
            </Text>
          )}

          <TextInput
            label="Amount (Satoshis)"
            value={satoshisAmount}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setSatoshisAmount(value);
              validateSatoshisAmount(value);
            }}
            error={error}
          />
          <SatoshisInfo cubby={cubby} satoshis={parseInt(satoshisAmount)} />
          {cubby.stagedLightningAddressDataForPayment.data
            .commentLengthAllowed && (
            <TextInput
              label="Message For Recipient"
              value={comment}
              placeholder="Optional"
              onChange={(event) => setComment(event.currentTarget.value)}
              error={
                comment.length >
                cubby.stagedLightningAddressDataForPayment.data
                  .commentLengthAllowed
                  ? `Comment must be less than ${cubby.stagedLightningAddressDataForPayment.data.commentLengthAllowed} characters`
                  : null
              }
            />
          )}
          {cubby.shouldShow2FAFormForPayments && (
            <TextInput
              label="Enter 2FA Code"
              maxLength={6}
              placeholder="Get the code from your authenticator app"
              value={twoFACode}
              onChange={handleTwoFACodeChange}
              error={twoFAError}
              mt="md"
            />
          )}

          <Button
            onClick={handleSendPayment}
            loading={loading}
            disabled={isDisabled}
          >
            Send Payment
          </Button>
        </Stack>
      </Modal>
    </>
  );
});

export default ConfirmPayToLightningAddress;
